import React, { useEffect, useMemo, useState } from "react";
import {
    getFournisseurs,
    createFournisseur,
    updateFournisseur,
    deleteFournisseur,
    resetFournisseurPassword, // ✅ ajout
} from "../api/api";

import Table from "../components/Table";
import PasswordInput from "../components/PasswordInput";
import { Button, Form, Container, Modal, Spinner, Alert } from "react-bootstrap";
import { useNotifications } from "../components/NotificationsContext";

export default function FournisseursList() {
    const { addNotification } = useNotifications();

    const [list, setList] = useState([]);
    const [page, setPage] = useState(1);
    const perPage = 5;

    // Modal create/edit
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({
        name: "",
        login: "",
        is_active: true
    });
    // Afficher temp_password après create
    const [tempPassword, setTempPassword] = useState(null);

    // ✅ Modal reset password
    const [showPwdModal, setShowPwdModal] = useState(false);
    const [pwdTarget, setPwdTarget] = useState(null);
    const [pwd, setPwd] = useState("");
    const [pwdResult, setPwdResult] = useState(null);
    const [pwdLoading, setPwdLoading] = useState(false);

    const columns = useMemo(
        () => [
            { Header: "Nom", accessor: "name" },
            { Header: "Login", accessor: "login" },
            { Header: "Actif", accessor: (row) => (row.is_active ? "Oui" : "Non") },
        ],
        []
    );
    const load = async () => {
        try {
            const res = await getFournisseurs();
            setList(res.data);
        } catch (err) {
            console.error(err);
            addNotification({
                type: "error",
                title: "Erreur",
                message: "Impossible de charger les fournisseurs.",
            });
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreate = () => {
        setSelected(null);
        setForm({ name: "", login: "", is_active: true }); setErrors({});
        setTempPassword(null);
        setShowModal(true);
    };

    const openEdit = (row) => {
        setSelected(row);
        setForm({
            name: row.name || "",
            login: row.login || "",
            is_active: !!row.is_active
        }); setErrors({});
        setTempPassword(null);
        setShowModal(true);
    };

    // ✅ ouvrir modal reset mdp
    const openResetPwd = (row) => {
        setPwdTarget(row);
        setPwd("");
        setPwdResult(null);
        setShowPwdModal(true);
    };

    const validate = () => {
        const e = {};
        if (!form.login.trim()) e.login = "Login requis";
        if (!form.name.trim()) e.name = "Nom requis";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        try {
            setSaving(true);
            const payload = {
                name: form.name.trim(),
                login: form.login.trim(),
                is_active: form.is_active
            };

            if (selected?.id) {
                await updateFournisseur(selected.id, payload);
                setList((prev) =>
                    prev.map((x) => (x.id === selected.id ? { ...x, ...payload } : x))
                );
                addNotification({ type: "success", title: "Succès", message: "Fournisseur mis à jour." });
                setShowModal(false);
            } else {
                // create renvoie temp_password
                const res = await createFournisseur(payload);
                setTempPassword(res.data?.temp_password || null);
                addNotification({ type: "success", title: "Créé", message: "Fournisseur créé." });

                // recharger la liste pour récupérer l'id etc.
                await load();
            }
        } catch (err) {
            console.error(err.response?.data || err);
            setErrors((prev) => ({ ...prev, ...(err?.response?.data || {}) }));
            addNotification({
                type: "error",
                title: "Erreur",
                message: err?.response?.data?.detail || "Opération échouée.",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Supprimer ${row.login} ?`)) return;

        try {
            await deleteFournisseur(row.id);
            setList((prev) => prev.filter((x) => x.id !== row.id));
            addNotification({ type: "success", title: "Suppression", message: "Fournisseur supprimé." });
        } catch (err) {
            console.error(err.response?.data || err);
            addNotification({ type: "error", title: "Erreur", message: "Échec suppression." });
        }
    };

    // ✅ reset password
    const handleResetPwd = async () => {
        if (!pwdTarget?.id) return;

        try {
            setPwdLoading(true);

            // si pwd est vide -> backend génère temp_password
            const res = await resetFournisseurPassword(pwdTarget.id, pwd.trim() || null);

            setPwdResult(res.data?.temp_password || "(mot de passe défini)");
            addNotification({ type: "success", title: "OK", message: "Mot de passe réinitialisé." });

            // Option: forcer refresh liste
            await load();
        } catch (err) {
            console.error(err.response?.data || err);
            addNotification({
                type: "error",
                title: "Erreur",
                message: err?.response?.data?.detail || "Reset échoué.",
            });
        } finally {
            setPwdLoading(false);
        }
    };

    return (
        <Container style={{ maxWidth: 900 }} className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Fournisseurs</h4>
                <Button onClick={openCreate} variant="success">
                    ➕ Ajouter
                </Button>
            </div>

            {/* Modal Create/Edit */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{selected?.id ? "Modifier fournisseur" : "Ajouter fournisseur"}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {tempPassword && (
                        <Alert variant="warning">
                            Mot de passe temporaire: <b>{tempPassword}</b>
                        </Alert>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>Nom *</Form.Label>
                        <Form.Control
                            placeholder="Ex: Ooredoo, Tunisianet..."
                            value={form.name}
                            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                            isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {Array.isArray(errors.name) ? errors.name[0] : errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Login *</Form.Label>
                        <Form.Control
                            placeholder="Email ou 8 chiffres"
                            value={form.login}
                            onChange={(e) => setForm((p) => ({ ...p, login: e.target.value }))}
                            isInvalid={!!errors.login}
                        />
                        <Form.Control.Feedback type="invalid">
                            {Array.isArray(errors.login) ? errors.login[0] : errors.login}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Check
                        type="switch"
                        label="Actif"
                        checked={form.is_active}
                        onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                    />
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
                        Annuler
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" className="me-2" /> : null}
                        Enregistrer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ✅ Modal Reset Password */}
            <Modal show={showPwdModal} onHide={() => setShowPwdModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Réinitialiser mot de passe — {pwdTarget?.login}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Nouveau mot de passe (optionnel)</Form.Label>
                        <PasswordInput
                            inputClassName="form-control"
                            placeholder="Laisser vide pour générer automatiquement"
                            value={pwd}
                            onChange={(e) => setPwd(e.target.value)}
                        />
                    </Form.Group>

                    {pwdResult && (
                        <Alert variant="warning">
                            Mot de passe temporaire: <b>{pwdResult}</b>
                        </Alert>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPwdModal(false)} disabled={pwdLoading}>
                        Fermer
                    </Button>
                    <Button variant="primary" onClick={handleResetPwd} disabled={pwdLoading}>
                        {pwdLoading ? <Spinner size="sm" className="me-2" /> : null}
                        Reset
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Table */}
            <Table
                data={list}
                columns={columns}
                onEdit={openEdit}
                onDelete={handleDelete}
                onResetPassword={openResetPwd}   // ✅ IMPORTANT: à ajouter dans Table.jsx
                currentPage={page}
                setPage={setPage}
                perPage={perPage}
            />
        </Container>
    );
}
